/**
 * +glide: the canopy over a gliding hero: a soft, rounded paraglider–parachute hybrid in the toon
 * style. Cloth panels alternate a warm sail cream with the hero's class tint (a darker seam band at
 * the hem, leaf-like scalloped trailing edge), a dark outline hull, and eight lines down to the raised
 * hands. It pops in with the GlideLayer's spring (anim/glide.ts), sways and banks with steering,
 * and folds away on release or landing. One shared geometry; materials per hero (owned).
 */
import * as THREE from 'three';
/** The hero's height (yd; units.ts HERO_HEIGHT; not imported: units.ts imports this module). */
const HERO_HEIGHT = 2.3;
import { ownedToonMaterial } from './style';

/** Canopy shape (yards): half-width (across), half-depth (along the flight), crown height, hang height over the root. */
const SPAN = 2.25;
const DEPTH = 1.05;
const CROWN = 0.85;
const HANG = HERO_HEIGHT + 1.75;
const SEGS_A = 22;
const SEGS_R = 7;
const SAIL = new THREE.Color(0xf6e7c8);

let sharedGeo: THREE.BufferGeometry | null = null;
let sharedHull: THREE.BufferGeometry | null = null;

/** A dome cap: across = local z, along = local x (the hero's forward), vertex colours mark the panels. */
function canopyGeometry(): THREE.BufferGeometry {
  if (sharedGeo) return sharedGeo;
  const pos: number[] = [];
  const col: number[] = [];
  const idx: number[] = [];
  for (let r = 0; r <= SEGS_R; r++) {
    const v = r / SEGS_R; // 0 crown .. 1 hem
    const ring = Math.sin(v * Math.PI * 0.5);
    for (let a = 0; a <= SEGS_A; a++) {
      const t = (a / SEGS_A) * Math.PI * 2;
      // Scalloped hem: the trailing (back) half dips between the ribs, like a leaf's edge.
      const scallop = v > 0.85 ? 0.06 * Math.max(0, -Math.cos(t)) * Math.abs(Math.sin(t * 5)) : 0;
      const x = Math.cos(t) * DEPTH * ring;
      const z = Math.sin(t) * SPAN * ring;
      const y = CROWN * Math.cos(v * Math.PI * 0.5) - scallop - 0.18 * (z / SPAN) ** 2; // the wing tips droop
      pos.push(x, y, z);
      // Panels: alternate across the span; the hem band darker.
      const panel = Math.floor(((Math.atan2(z / SPAN, x / DEPTH || 1e-6) + Math.PI) / (Math.PI * 2)) * 10) % 2;
      const hem = v > 0.82 ? 0.78 : 1;
      col.push(panel, hem, 0);
    }
  }
  for (let r = 0; r < SEGS_R; r++)
    for (let a = 0; a < SEGS_A; a++) {
      const i = r * (SEGS_A + 1) + a;
      const j = i + SEGS_A + 1;
      idx.push(i, j, i + 1, i + 1, j, j + 1);
    }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('panel', new THREE.Float32BufferAttribute(col, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  sharedGeo = g;
  // The outline hull: the same cap pushed out along its normals.
  const hull = g.clone();
  const p = hull.getAttribute('position') as THREE.BufferAttribute;
  const n = hull.getAttribute('normal') as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) p.setXYZ(i, p.getX(i) + n.getX(i) * 0.05, p.getY(i) + n.getY(i) * 0.05, p.getZ(i) + n.getZ(i) * 0.05);
  sharedHull = hull;
  return g;
}

/** The hand grips (hero-root space) the lines run to: hands raised overhead. */
const GRIP_L = new THREE.Vector3(0.12, HERO_HEIGHT * 0.98 + 0.28, 0.24);
const GRIP_R = new THREE.Vector3(0.12, HERO_HEIGHT * 0.98 + 0.28, -0.24);

export class GliderRig {
  readonly group = new THREE.Group();
  private readonly canopy = new THREE.Group();
  private readonly mat: THREE.MeshToonMaterial;
  private readonly hullMat: THREE.MeshBasicMaterial;
  private readonly lines: THREE.LineSegments;
  private readonly linePos: Float32Array;
  private t = Math.random() * 10;

  constructor(tint: string | number) {
    const geo = canopyGeometry();
    // Warm sail cream and the class tint (softened toward the sail so it never reads garish).
    const tintC = new THREE.Color(tint).lerp(SAIL, 0.25);
    const colors = new Float32Array((geo.getAttribute('panel') as THREE.BufferAttribute).count * 3);
    const panel = geo.getAttribute('panel') as THREE.BufferAttribute;
    const c = new THREE.Color();
    for (let i = 0; i < panel.count; i++) {
      c.copy(panel.getX(i) > 0.5 ? tintC : SAIL).multiplyScalar(panel.getY(i));
      colors.set([c.r, c.g, c.b], i * 3);
    }
    const g = geo.clone();
    g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    this.mat = ownedToonMaterial({ color: 0xffffff, vertexColors: true, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(g, this.mat);
    mesh.castShadow = true;
    this.hullMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(tint).multiply(new THREE.Color(0.22, 0.17, 0.3)), side: THREE.BackSide });
    const hull = new THREE.Mesh(sharedHull!, this.hullMat);
    this.canopy.add(mesh, hull);
    this.canopy.position.set(0.1, HANG, 0);
    // Eight lines: four rim points per side down to that side's grip.
    this.linePos = new Float32Array(8 * 2 * 3);
    const lg = new THREE.BufferGeometry();
    lg.setAttribute('position', new THREE.BufferAttribute(this.linePos, 3));
    this.lines = new THREE.LineSegments(lg, new THREE.LineBasicMaterial({ color: 0x4a3a32, transparent: true, opacity: 0.85 }));
    this.lines.frustumCulled = false;
    this.group.add(this.canopy, this.lines);
    this.group.visible = false;
  }

  /** `open` 0..1.25 (the GlideLayer's canopy spring), `bank` radians, `speed` yd/s. */
  update(open: number, bank: number, speed: number, dtS: number): void {
    const vis = open > 0.01;
    this.group.visible = vis;
    if (!vis) return;
    this.t += dtS;
    // Deploy: unfurl from a bundle over the shoulders (narrow and low first, the span last).
    const o = Math.max(0, open);
    const span = Math.min(1.15, o * o * (1.25 - 0.25 * o) + 0.05);
    const lift = Math.min(1, o * 1.4);
    this.canopy.scale.set(0.35 + 0.65 * Math.min(1, o), 0.3 + 0.7 * lift, span);
    this.canopy.position.y = HERO_HEIGHT * 0.95 + (HANG - HERO_HEIGHT * 0.95) * lift;
    // Banking: the wing rolls into the turn; a slow breathing sway; pitched forward with speed.
    this.canopy.rotation.x = bank * 0.7 + Math.sin(this.t * 1.1) * 0.035;
    this.canopy.rotation.z = -0.06 - Math.min(0.18, speed * 0.012) + Math.sin(this.t * 0.8 + 1) * 0.02;
    this.canopy.updateMatrix();
    // Lines: rim points (in canopy space) → grips (root space).
    const rim = [-0.92, -0.62, -0.32, -0.08];
    let k = 0;
    const v = new THREE.Vector3();
    for (const side of [1, -1] as const) {
      const grip = side > 0 ? GRIP_L : GRIP_R;
      for (const f of rim) {
        const zz = side * SPAN * Math.abs(f) * 0.95;
        v.set(f * DEPTH * 0.25, -0.02 - 0.18 * (zz / SPAN) ** 2, zz).applyMatrix4(this.canopy.matrix);
        this.linePos.set([v.x, v.y, v.z, grip.x, grip.y, grip.z], k);
        k += 6;
      }
    }
    (this.lines.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.lines.material as THREE.LineBasicMaterial).opacity = 0.85 * Math.min(1, o);
  }

  dispose(): void {
    this.group.removeFromParent();
    this.mat.dispose();
    this.hullMat.dispose();
    for (const m of this.canopy.children as THREE.Mesh[]) if (m.geometry !== sharedHull) m.geometry.dispose();
    this.lines.geometry.dispose();
    (this.lines.material as THREE.Material).dispose();
  }
}
